"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Image } from "antd";
import dynamic from "next/dynamic";
import { UserData } from "@/lib/api/types";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ReactPlayer from "react-player";
import { track } from "@vercel/analytics";

const Slider = dynamic(() => import("react-slick"), { ssr: false });

interface DisplaySliderProps {
  user: {
    name: string;
    _id: string;
  };
  photos: UserData["photos"];
  slides: number;
}

const sliderDefaults = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToScroll: 1,
};

const DisplaySlider = ({ user, photos, slides }: DisplaySliderProps) => {
  const [photoId, setPhotoId] = useState<string | null>(null);

  // Read from URL only on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idFromUrl = params.get("photoId");
    if (idFromUrl) setPhotoId(idFromUrl);
  }, []);

  const sliderSettings = useMemo(
    () => ({
      ...sliderDefaults,
      slidesToShow: slides,
    }),
    [slides]
  );

  const onChangePreview = useCallback(
    (id: string) => {
      if (!id) return;

      const url = new URL(window.location.href);
      url.searchParams.set("photoId", id);
      window.history.replaceState(null, "", url.toString());

      setPhotoId(id);

      track("PhotoViewInStylistPage", {
        photoId: id,
        ownerName: user.name,
        ownerId: user._id,
      });
    },
    [user.name, user._id]
  );

  const onClosePreview = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("photoId");
    window.history.replaceState(null, "", url.toString());
    setPhotoId(null);
  }, []);

  const previewSettings = useMemo(() => {
    const currentIndex = photoId
      ? photos.findIndex((photo) => photo._id === photoId)
      : 0;

    return {
      visible: !!photoId,
      current: currentIndex !== -1 ? currentIndex : 0,
      toolbarRender: () => null,
      imageRender: (
        originNode: React.ReactNode,
        { current }: { current: number }
      ) => {
        const currentPhoto = photos[current];
        if (currentPhoto?.type === "video") {
          return (
            <ReactPlayer
              url={currentPhoto.src}
              playing
              controls={false}
              muted
              loop
              width="100%"
              height="100%"
            />
          );
        }
        return originNode;
      },
      onChange: (current: number) => {
        const selectedPhoto = photos[current];
        if (selectedPhoto?._id) onChangePreview(selectedPhoto._id);
      },
      onVisibleChange: (visible: boolean) => {
        if (!visible) onClosePreview();
      },
    };
  }, [photoId, photos, onChangePreview, onClosePreview]);

  return (
    <div
      id="DisplaySlider"
      className="is-relative"
      style={{ minHeight: "180px" }}
    >
      <Image.PreviewGroup items={photos} preview={previewSettings}>
        <Slider {...sliderSettings}>
          {photos.map((photo, index) => (
            <Image
              key={index}
              src={
                photo.type === "video"
                  ? photo.variants?.[0] || "/logo.png"
                  : photo.src
                  ? photo.src
                  : "/logo.png"
              }
              alt={photo.alt || "Photo"}
              width="100%"
              height="180px"
              preview={{ mask: <div></div> }}
              onClick={() => onChangePreview(photo._id)}
            />
          ))}
        </Slider>
      </Image.PreviewGroup>
    </div>
  );
};

export default DisplaySlider;
